import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Cpu, Zap, Rocket, Phone, Mail, Globe, Sparkles, Code2, Database, Shield } from 'lucide-react';

// TimeBlock component
const TimeBlock = ({ value, label, colorClass }) => (
  <motion.div 
    className="flex flex-col items-center"
    whileHover={{ scale: 1.05 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="relative">
      <div className={`absolute -inset-2 ${colorClass} rounded-2xl blur-xl opacity-40`} />
      <div className="relative w-20 h-24 sm:w-28 sm:h-32 lg:w-32 lg:h-36 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-white/5" />
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span 
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tabular-nums tracking-tight"
            style={{ textShadow: '0 0 30px rgba(255,255,255,0.3)' }}
          >
            {String(value).padStart(2, '0')}
          </span>
        </div>
        <motion.div
          className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60"
          animate={{ top: ['0%', '100%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
      </div>
    </div>
    <span className="mt-3 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
      {label}
    </span>
  </motion.div>
);

// Separator
const TimeSeparator = () => (
  <div className="flex flex-col gap-3 mt-[-15px]">
    <motion.div 
      className="w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50"
      animate={{ opacity: [1, 0.3, 1] }}
      transition={{ duration: 1, repeat: Infinity }}
    />
    <motion.div 
      className="w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1, repeat: Infinity }}
    />
  </div>
);

// Tech feature pill
const TechFeature = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 rounded-full border border-slate-700/50 backdrop-blur-sm hover:scale-105 hover:border-cyan-500/50 transition-all duration-300 cursor-default">
    <Icon className="w-4 h-4 text-cyan-400" />
    <span className="text-xs sm:text-sm font-medium text-slate-300">{title}</span>
  </div>
);

// Floating icon
const FloatingIcon = ({ icon: Icon, delay, x, y, color }) => (
  <motion.div
    className={`absolute ${color}`}
    style={{ left: `${x}%`, top: `${y}%`, opacity: 0.15 }}
    animate={{
      y: [-15, 15, -15],
      rotate: [0, 5, -5, 0],
    }}
    transition={{
      duration: 5,
      delay: delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    <Icon size={36} />
  </motion.div>
);

// Particle
const Particle = ({ delay, startX }) => (
  <motion.div
    className="absolute w-1 h-1 bg-cyan-400 rounded-full"
    style={{ left: `${startX}%`, bottom: 0 }}
    animate={{
      y: [0, -400, -800],
      opacity: [0, 1, 0],
      scale: [0, 1.5, 0],
    }}
    transition={{
      duration: 4,
      delay: delay,
      repeat: Infinity,
      ease: "easeOut"
    }}
  />
);

export default function ComingSoon() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const targetDate = useMemo(() => {
    const now = new Date();
    const target = new Date(now);
    target.setDate(target.getDate() + 1);
    target.setHours(12, 0, 0, 0);
    return target;
  }, []);

  const calculateTimeLeft = useCallback(() => {
    const now = new Date();
    const difference = targetDate.getTime() - now.getTime();
    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }, [targetDate]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  const particles = useMemo(() => 
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: Math.random() * 4,
      startX: Math.random() * 100,
    })),
  []);

  return (
    <div className="min-h-screen w-full bg-slate-950 relative overflow-x-hidden">
      {/* Animated gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Moving gradient orbs */}
      <motion.div
        className="fixed w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
          left: '10%',
          top: '20%',
        }}
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="fixed w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          right: '10%',
          bottom: '20%',
        }}
        animate={{ x: [0, -80, 0], y: [0, -60, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Grid overlay */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />

      {/* Floating particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <Particle key={p.id} delay={p.delay} startX={p.startX} />
        ))}
      </div>

      {/* Floating tech icons */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none hidden sm:block">
        <FloatingIcon icon={Cpu} delay={0} x={8} y={20} color="text-cyan-400" />
        <FloatingIcon icon={Zap} delay={1} x={88} y={12} color="text-yellow-400" />
        <FloatingIcon icon={Globe} delay={2} x={12} y={65} color="text-blue-400" />
        <FloatingIcon icon={Rocket} delay={0.5} x={85} y={70} color="text-purple-400" />
        <FloatingIcon icon={Code2} delay={1.5} x={92} y={40} color="text-green-400" />
        <FloatingIcon icon={Database} delay={2.5} x={5} y={45} color="text-pink-400" />
        <FloatingIcon icon={Shield} delay={0.8} x={50} y={85} color="text-orange-400" />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-12">
        
        {/* Logo */}
        <motion.div
          className="mb-6 sm:mb-8"
          animate={{ 
            filter: [
              'drop-shadow(0 0 20px rgba(6, 182, 212, 0.4))', 
              'drop-shadow(0 0 40px rgba(6, 182, 212, 0.7))', 
              'drop-shadow(0 0 20px rgba(6, 182, 212, 0.4))'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <img 
            src="/logo-white.png" 
            alt="RAQ Technologies" 
            className="h-16 sm:h-24 lg:h-32 w-auto"
          />
        </motion.div>

        {/* Main title */}
        <div className="text-center mb-3 sm:mb-4">
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-white tracking-tight leading-tight">
            GRANDE RÉVÉLATION
          </h1>
        </div>

        {/* Subtitle with sparkles */}
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
          </motion.div>
          <p className="text-base sm:text-xl lg:text-2xl text-cyan-400 font-semibold tracking-wide">
            Notre site se transforme
          </p>
          <motion.div animate={{ rotate: -360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
          </motion.div>
        </div>

        {/* Tech message */}
        <div className="text-center mb-6 sm:mb-8 max-w-2xl px-4">
          <p className="text-slate-400 text-sm sm:text-lg leading-relaxed mb-4 sm:mb-6">
            Nous intégrons de <span className="text-cyan-400 font-semibold">nouvelles technologies</span> pour vous offrir 
            une expérience révolutionnaire. Préparez-vous pour le futur de la location d&apos;équipements!
          </p>
          
          {/* Tech features */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            <TechFeature icon={Rocket} title="Réservation en ligne" />
            <TechFeature icon={Zap} title="Paiements instantanés" />
            <TechFeature icon={Globe} title="Disponibilité en direct" />
          </div>
        </div>

        {/* Countdown section */}
        <div className="mb-6 sm:mb-8">
          <motion.p 
            className="text-center text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-slate-500 mb-4 sm:mb-6 font-medium"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Lancement dans
          </motion.p>
          
          <div className="flex items-center justify-center gap-2 sm:gap-4 lg:gap-6">
            <TimeBlock value={timeLeft.days} label="Jours" colorClass="bg-cyan-500" />
            <TimeSeparator />
            <TimeBlock value={timeLeft.hours} label="Heures" colorClass="bg-blue-500" />
            <TimeSeparator />
            <TimeBlock value={timeLeft.minutes} label="Minutes" colorClass="bg-purple-500" />
            <TimeSeparator />
            <TimeBlock value={timeLeft.seconds} label="Secondes" colorClass="bg-pink-500" />
          </div>
        </div>

        {/* Contact info */}
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
          <motion.a
            href="tel:4508760989"
            className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-all duration-300 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="p-2 rounded-lg bg-slate-800/50 group-hover:bg-cyan-500/20 transition-colors border border-slate-700/50">
              <Phone className="w-4 h-4" />
            </div>
            <span className="font-medium text-sm sm:text-base">450-876-0989</span>
          </motion.a>
          
          <div className="hidden sm:block w-px h-6 bg-slate-700" />
          
          <motion.a
            href="mailto:info@raq-canada.com"
            className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-all duration-300 group"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="p-2 rounded-lg bg-slate-800/50 group-hover:bg-cyan-500/20 transition-colors border border-slate-700/50">
              <Mail className="w-4 h-4" />
            </div>
            <span className="font-medium text-sm sm:text-base">info@raq-canada.com</span>
          </motion.a>
        </div>
      </div>

      {/* Corner accents */}
      <div className="fixed top-0 left-0 w-20 sm:w-32 h-20 sm:h-32 border-l-2 border-t-2 border-cyan-500/30 pointer-events-none z-20" />
      <div className="fixed top-0 right-0 w-20 sm:w-32 h-20 sm:h-32 border-r-2 border-t-2 border-cyan-500/30 pointer-events-none z-20" />
      <div className="fixed bottom-0 left-0 w-20 sm:w-32 h-20 sm:h-32 border-l-2 border-b-2 border-cyan-500/30 pointer-events-none z-20" />
      <div className="fixed bottom-0 right-0 w-20 sm:w-32 h-20 sm:h-32 border-r-2 border-b-2 border-cyan-500/30 pointer-events-none z-20" />

      {/* Animated border line */}
      <motion.div
        className="fixed bottom-0 left-0 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 z-30"
        initial={{ width: '0%' }}
        animate={{ width: '100%' }}
        transition={{ duration: 2.5, ease: "easeOut" }}
      />
    </div>
  );
}
